import React, { useMemo, useState } from 'react';

export interface AvailablePackage {
  id: string;
  packageCode: string;
  name: string;
  priceWeekly: number;
  priceMonthly: number;
  billingModel: string;
  isActive: boolean;
  displayOrder: number;
}

export type BillingCycleValue = 'WEEKLY' | 'MONTHLY';

interface ChangePlanModalProps {
  currentPackageCode: string;
  currentBillingCycle: string; // 'weekly' | 'monthly' (lowercase from API)
  availablePackages: AvailablePackage[];
  effectiveDateText: string;
  loading: boolean;
  errorMessage?: string;
  formatCurrency: (amount: number) => string;
  onConfirm: (newPackageId: number, newBillingCycle: BillingCycleValue) => void;
  onClose: () => void;
}

/**
 * Lets a user switch their single active recurring subscription to a different
 * package and/or billing cycle. The change is queued and takes effect at the end
 * of the current period — no immediate charge.
 */
const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  currentPackageCode,
  currentBillingCycle,
  availablePackages,
  effectiveDateText,
  loading,
  errorMessage,
  formatCurrency,
  onConfirm,
  onClose,
}) => {
  // Only recurring, active packages can be switched to.
  const selectablePackages = useMemo(
    () =>
      availablePackages
        .filter((p) => p.isActive && p.billingModel?.toLowerCase() !== 'one_time')
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [availablePackages]
  );

  const [selectedPackageId, setSelectedPackageId] = useState<string>(() => {
    const current = selectablePackages.find((p) => p.packageCode === currentPackageCode);
    return current ? current.id : selectablePackages[0]?.id ?? '';
  });
  const [cycle, setCycle] = useState<BillingCycleValue>(
    currentBillingCycle?.toLowerCase() === 'monthly' ? 'MONTHLY' : 'WEEKLY'
  );

  const selectedPackage = selectablePackages.find((p) => p.id === selectedPackageId);
  const newPrice = selectedPackage
    ? cycle === 'MONTHLY'
      ? selectedPackage.priceMonthly
      : selectedPackage.priceWeekly
    : 0;

  const isNoop =
    selectedPackage?.packageCode === currentPackageCode &&
    cycle === (currentBillingCycle?.toLowerCase() === 'monthly' ? 'MONTHLY' : 'WEEKLY');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
        <h3 className="text-base sm:text-lg font-semibold text-racing-green mb-4">Change Plan</h3>

        <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
        <select
          value={selectedPackageId}
          onChange={(e) => setSelectedPackageId(e.target.value)}
          disabled={loading}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2 focus:ring-racing-green"
        >
          {selectablePackages.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.packageCode === currentPackageCode ? ' (current plan)' : ''}
            </option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Billing cycle</label>
        <div className="flex gap-2 mb-4">
          {(['WEEKLY', 'MONTHLY'] as BillingCycleValue[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCycle(c)}
              disabled={loading}
              className={`flex-1 py-2 px-3 rounded text-sm border transition ${
                cycle === c
                  ? 'bg-racing-green text-white border-racing-green'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-racing-green'
              }`}
            >
              {c === 'WEEKLY' ? 'Weekly' : 'Monthly'}
            </button>
          ))}
        </div>

        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
          <div className="flex justify-between">
            <span>New price</span>
            <span className="font-semibold">
              {formatCurrency(newPrice)} / {cycle === 'MONTHLY' ? 'month' : 'week'}
            </span>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            This change takes effect on <strong>{effectiveDateText}</strong>. You won't be charged
            now — your new price applies from your next billing date.
          </p>
        </div>

        {errorMessage && (
          <div className="mb-4 p-2 rounded bg-red-50 text-red-800 border border-red-200 text-sm">
            {errorMessage}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => selectedPackage && onConfirm(parseInt(selectedPackage.id, 10), cycle)}
            disabled={loading || isNoop || !selectedPackage}
            className={`bg-racing-green text-white py-2 px-4 rounded hover:bg-racing-green-dark transition flex items-center justify-center text-sm ${
              loading || isNoop || !selectedPackage ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Scheduling…' : isNoop ? 'Select a different plan' : 'Confirm change'}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePlanModal;
